/**
 * F60 — shared helpers for the ops pages (/ops/logs, /ops/health): time-range
 * presets, URL ⇄ search state, time / duration / size formatting, tones, and
 * (F60b) the health timeline's bins, ticks, verdict and thresholds.
 */
import type {
  OpsLatencyReport,
  OpsLogEvent,
  OpsLogLevel,
  OpsLogsFilters,
  OpsLogsStats,
} from '@bravemove/types';
import type { BadgeTone } from '@bravemove/ui-web';
import type { OpsRange } from '../../api/opsLogs';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

export const LOGS_PRESETS = ['15m', '1h', '6h', '24h', '7d'] as const;
export type LogsPreset = (typeof LOGS_PRESETS)[number] | 'custom';

export const HEALTH_PRESETS = ['1h', '6h', '24h', '7d'] as const;
export type HealthPreset = (typeof HEALTH_PRESETS)[number];

const PRESET_MS: Record<(typeof LOGS_PRESETS)[number], number> = {
  '15m': 15 * MINUTE,
  '1h': HOUR,
  '6h': 6 * HOUR,
  '24h': 24 * HOUR,
  '7d': 7 * 24 * HOUR,
};

/** Log search runs up to "now" (rounded up to the minute; the API clamps it). */
export function rangeForLogsPreset(
  preset: (typeof LOGS_PRESETS)[number],
  now = Date.now(),
): OpsRange {
  const to = Math.ceil(now / MINUTE) * MINUTE;
  return { from: new Date(to - PRESET_MS[preset]).toISOString(), to: new Date(to).toISOString() };
}

/** Health ranges end on a whole minute so reloads within it hit the API cache. */
export function rangeForHealthPreset(preset: HealthPreset, now = Date.now()): OpsRange {
  const to = Math.floor(now / MINUTE) * MINUTE;
  return { from: new Date(to - PRESET_MS[preset]).toISOString(), to: new Date(to).toISOString() };
}

// ─── Search state ⇄ URL ──────────────────────────────────────────────────────

export interface LogsSearch {
  readonly preset: LogsPreset;
  /** ISO — only for `custom`. */
  readonly from?: string;
  readonly to?: string;
  readonly filters: OpsLogsFilters;
}

const FILTER_KEYS = [
  'minLevel',
  'method',
  'route',
  'status',
  'requestId',
  'userId',
  'adminId',
  'text',
] as const;

export function searchFromParams(params: URLSearchParams): LogsSearch {
  const presetParam = params.get('preset');
  const preset: LogsPreset =
    presetParam === 'custom' || (LOGS_PRESETS as readonly string[]).includes(presetParam ?? '')
      ? (presetParam as LogsPreset)
      : '1h';
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value) filters[key] = value;
  }
  const from = params.get('from');
  const to = params.get('to');
  return {
    preset: preset === 'custom' && (!from || !to) ? '1h' : preset,
    ...(preset === 'custom' && from && to ? { from, to } : {}),
    filters: filters as OpsLogsFilters,
  };
}

export function paramsFromSearch(search: LogsSearch): URLSearchParams {
  const params = new URLSearchParams();
  params.set('preset', search.preset);
  if (search.preset === 'custom' && search.from && search.to) {
    params.set('from', search.from);
    params.set('to', search.to);
  }
  for (const key of FILTER_KEYS) {
    const value = search.filters[key];
    if (value) params.set(key, value);
  }
  return params;
}

export function rangeForSearch(search: LogsSearch, now = Date.now()): OpsRange {
  if (search.preset === 'custom' && search.from && search.to) {
    return { from: search.from, to: search.to };
  }
  return rangeForLogsPreset(search.preset === 'custom' ? '1h' : search.preset, now);
}

/** Live tail only follows the newest lines unfiltered (a level floor is fine). */
export function canLiveTail(search: LogsSearch): boolean {
  if (search.preset === 'custom') return false;
  return FILTER_KEYS.every((key) => key === 'minLevel' || !search.filters[key]);
}

/** Prepend freshly tailed lines, dropping ids already shown; keeps the list bounded. */
export function mergeNewest(
  fresh: readonly OpsLogEvent[],
  current: readonly OpsLogEvent[],
  cap = 2_000,
): OpsLogEvent[] {
  const seen = new Set(current.map((e) => e.id));
  const added = fresh.filter((e) => !seen.has(e.id));
  return [...added, ...current].slice(0, cap);
}

/** Append an older page, dropping ids already shown. */
export function appendOlder(
  current: readonly OpsLogEvent[],
  older: readonly OpsLogEvent[],
): OpsLogEvent[] {
  const seen = new Set(current.map((e) => e.id));
  return [...current, ...older.filter((e) => !seen.has(e.id))];
}

// ─── Formatting ──────────────────────────────────────────────────────────────

export type TimeZoneMode = 'local' | 'utc';
const TZ_STORAGE_KEY = 'bravemove.admin.opsLogs.timeZone';

export function readTimeZoneMode(): TimeZoneMode {
  try {
    return window.localStorage.getItem(TZ_STORAGE_KEY) === 'utc' ? 'utc' : 'local';
  } catch {
    return 'local';
  }
}

export function writeTimeZoneMode(mode: TimeZoneMode): void {
  try {
    window.localStorage.setItem(TZ_STORAGE_KEY, mode);
  } catch {
    // Private mode / blocked storage — the toggle still works for this visit.
  }
}

export function formatLogTime(
  iso: string,
  mode: TimeZoneMode,
  locale: string,
  withDate: boolean,
): string {
  return new Intl.DateTimeFormat(locale, {
    ...(withDate ? { month: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
    hourCycle: 'h23',
    ...(mode === 'utc' ? { timeZone: 'UTC' } : {}),
  }).format(new Date(iso));
}

/** Chart axis label for a bin start. */
export function formatBin(
  iso: string,
  binSeconds: number,
  mode: TimeZoneMode,
  locale: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    ...(binSeconds >= 3600 ? { month: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(mode === 'utc' ? { timeZone: 'UTC' } : {}),
  }).format(new Date(iso));
}

/** Milliseconds with one decimal below 10 ms, whole numbers above. */
export function roundMs(value: number): string {
  return value < 10 ? value.toFixed(1) : String(Math.round(value));
}

export function megabytes(bytes: number): string {
  return (bytes / 1_000_000).toFixed(bytes < 10_000_000 ? 1 : 0);
}

export function shortId(id: string): string {
  return id.slice(0, 8);
}

// ─── Tones ───────────────────────────────────────────────────────────────────

export function levelTone(level: OpsLogLevel | null): BadgeTone {
  switch (level) {
    case 'fatal':
    case 'error':
      return 'danger';
    case 'warn':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'neutral';
  }
}

export function statusTone(status: number | null): BadgeTone {
  if (status === null) return 'neutral';
  if (status >= 500) return 'danger';
  if (status >= 400) return 'warning';
  return 'success';
}

// ─── Errors ──────────────────────────────────────────────────────────────────

/** `ApiError` carries `.status/.code`; the test fixtures throw plain errors with the same fields. */
export function errorInfo(err: unknown): { status: number | null; code: string | null } {
  const e = err as { status?: unknown; code?: unknown } | null;
  return {
    status: typeof e?.status === 'number' ? e.status : null,
    code: typeof e?.code === 'string' ? e.code : null,
  };
}

const KNOWN_ERRORS = new Set([
  'OPS_LOGS_NOT_CONFIGURED',
  'OPS_LOGS_ACCESS_DENIED',
  'OPS_LOGS_RANGE_INVALID',
  'OPS_LOGS_RANGE_TOO_LARGE',
  'OPS_LOGS_ROUTE_UNKNOWN',
  'OPS_LOGS_CURSOR_INVALID',
  'OPS_LOGS_THROTTLED',
  'OPS_LOGS_QUERY_TIMEOUT',
  'OPS_LOGS_UPSTREAM',
  'VALIDATION_ERROR',
]);

/** i18n key under `admin:opsLogs.errors.*` for a failed call. */
export function errorKey(err: unknown): string {
  const { status, code } = errorInfo(err);
  if (status === 429 || code === 'RATE_LIMITED' || code === 'RATE_LIMIT_EXCEEDED')
    return 'RATE_LIMITED';
  if (code && KNOWN_ERRORS.has(code)) return code;
  return 'generic';
}

/** Save text as a file through a temporary object URL. */
export function downloadText(filename: string, chunks: readonly string[], type: string): void {
  const url = URL.createObjectURL(new Blob([...chunks], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── API health (F60b) ───────────────────────────────────────────────────────

/** One bin of the health timeline: the request, log and latency series merged by `t`. */
export interface HealthBin {
  readonly t: string;
  readonly requests: number;
  readonly s4xx: number;
  readonly s5xx: number;
  readonly warn: number;
  /** error + fatal (pino level ≥ 50) — the same definition as the email alarm. */
  readonly errors: number;
  readonly info: number;
  readonly p50: number | null;
  readonly p90: number | null;
  readonly p99: number | null;
}

type MutableBin = { -readonly [K in keyof HealthBin]: HealthBin[K] };

export function mergeHealthBins(stats: OpsLogsStats, latency: OpsLatencyReport): HealthBin[] {
  const rows = new Map<string, MutableBin>();
  const row = (t: string): MutableBin => {
    let r = rows.get(t);
    if (!r) {
      r = {
        t,
        requests: 0,
        s4xx: 0,
        s5xx: 0,
        warn: 0,
        errors: 0,
        info: 0,
        p50: null,
        p90: null,
        p99: null,
      };
      rows.set(t, r);
    }
    return r;
  };
  for (const b of stats.requests) {
    const r = row(b.t);
    r.requests = b.s2xx + b.s3xx + b.s4xx + b.s5xx;
    r.s4xx = b.s4xx;
    r.s5xx = b.s5xx;
  }
  for (const b of stats.levels) {
    const r = row(b.t);
    r.warn = b.warn;
    r.errors = b.error + b.fatal;
    r.info = b.info;
  }
  for (const b of latency.series) {
    const r = row(b.t);
    r.p50 = b.p50;
    r.p90 = b.p90;
    r.p99 = b.p99;
  }
  return [...rows.values()].sort((a, b) => Date.parse(a.t) - Date.parse(b.t));
}

/**
 * Drop the empty bins before the first request or log line (a fresh log group,
 * or a range reaching back before the first deploy), so charts start at data.
 * `dataFrom` is the first kept bin when anything was dropped.
 */
export function trimLeadingEmpty(bins: readonly HealthBin[]): {
  readonly bins: readonly HealthBin[];
  readonly dataFrom: string | null;
} {
  const first = bins.findIndex((b) => b.requests + b.warn + b.errors + b.info > 0);
  if (first === -1) return { bins: [], dataFrom: null };
  return { bins: bins.slice(first), dataFrom: first > 0 ? bins[first]!.t : null };
}

const TICK_STEPS_MS = [
  10 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  2 * HOUR,
  3 * HOUR,
  6 * HOUR,
  12 * HOUR,
  24 * HOUR,
] as const;

/**
 * Clean time-axis labels: the bins on a round boundary (10 min … 1 day — the
 * smallest step giving at most `maxLabels`) in the viewer's time zone.
 */
export function timeTicks(
  times: readonly string[],
  mode: TimeZoneMode,
  maxLabels = 8,
): { readonly ticks: string[]; readonly stepMs: number } {
  if (times.length === 0) return { ticks: [], stepMs: HOUR };
  const span = Date.parse(times[times.length - 1]!) - Date.parse(times[0]!);
  const stepMs = TICK_STEPS_MS.find((s) => span / s <= maxLabels) ?? 24 * HOUR;
  const stepMinutes = stepMs / MINUTE;
  const ticks = times.filter((iso) => {
    const d = new Date(iso);
    const minutes =
      mode === 'utc'
        ? d.getUTCHours() * 60 + d.getUTCMinutes()
        : d.getHours() * 60 + d.getMinutes();
    return minutes % stepMinutes === 0 && d.getUTCSeconds() === 0;
  });
  return { ticks, stepMs };
}

/** Axis label for a tick: the date for day steps, the time otherwise. */
export function formatTick(
  iso: string,
  stepMs: number,
  mode: TimeZoneMode,
  locale: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    ...(stepMs >= 24 * HOUR
      ? { month: 'short', day: 'numeric' }
      : { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
    ...(mode === 'utc' ? { timeZone: 'UTC' } : {}),
  }).format(new Date(iso));
}

/**
 * Health rules. The verdict uses the same numbers as the two email alarms
 * (docs/AWS_SETUP.md §10): more than 5 error lines or 3+ server errors = failing.
 */
export const HEALTH_THRESHOLDS = {
  windowMinutes: 15,
  failing5xx: 3,
  failingErrorLogs: 5,
  slowP90Ms: 1_000,
  rateWatchPct: 1,
  rateAlertPct: 5,
  p90WatchMs: 1_000,
  p90AlertMs: 3_000,
} as const;

export type HealthVerdictStatus = 'healthy' | 'degraded' | 'failing' | 'quiet';

export interface HealthVerdict {
  readonly status: HealthVerdictStatus;
  /** The window actually covered, in minutes, ending at the range end. */
  readonly minutes: number;
  readonly requests: number;
  readonly s5xx: number;
  readonly errors: number;
  /** Worst p90 among the window's bins. */
  readonly p90: number | null;
}

/** "Is the API healthy right now?" — from the bins overlapping the range's last 15 min. */
export function healthVerdict(
  bins: readonly HealthBin[],
  rangeEnd: string,
  binSeconds: number,
): HealthVerdict {
  const end = Date.parse(rangeEnd);
  const windowStart = end - HEALTH_THRESHOLDS.windowMinutes * MINUTE;
  // The API zero-fills bins up to the range end, so an empty window means no
  // data at all lately — which reads as "quiet".
  const recent = bins.filter((b) => Date.parse(b.t) + binSeconds * 1000 > windowStart);
  // Whole bins are counted, so the honest window can exceed 15 min (1 h bins on 7 d).
  const firstT = recent.length > 0 ? Date.parse(recent[0]!.t) : windowStart;
  const minutes = Math.max(1, Math.round((end - firstT) / MINUTE));
  const sum = (pick: (b: HealthBin) => number) => recent.reduce((n, b) => n + pick(b), 0);
  const requests = sum((b) => b.requests);
  const s5xx = sum((b) => b.s5xx);
  const errors = sum((b) => b.errors);
  const p90s = recent.map((b) => b.p90).filter((v): v is number => v !== null);
  const p90 = p90s.length > 0 ? Math.max(...p90s) : null;
  const status: HealthVerdictStatus =
    s5xx >= HEALTH_THRESHOLDS.failing5xx || errors > HEALTH_THRESHOLDS.failingErrorLogs
      ? 'failing'
      : s5xx > 0 || errors > 0 || (p90 ?? 0) >= HEALTH_THRESHOLDS.slowP90Ms
        ? 'degraded'
        : requests === 0
          ? 'quiet'
          : 'healthy';
  return { status, minutes, requests, s5xx, errors, p90 };
}

export type TileStatus = 'ok' | 'watch' | 'alert';

/** Share of requests that failed with a 5xx, in percent; `null` without traffic. */
export function errorPct(requests: number, s5xx: number): number | null {
  return requests > 0 ? (s5xx / requests) * 100 : null;
}

export function rateStatus(pct: number | null): TileStatus | null {
  if (pct === null) return null;
  if (pct >= HEALTH_THRESHOLDS.rateAlertPct) return 'alert';
  return pct >= HEALTH_THRESHOLDS.rateWatchPct ? 'watch' : 'ok';
}

export function p90Status(p90: number | null): TileStatus | null {
  if (p90 === null) return null;
  if (p90 >= HEALTH_THRESHOLDS.p90AlertMs) return 'alert';
  return p90 >= HEALTH_THRESHOLDS.p90WatchMs ? 'watch' : 'ok';
}

/** Any error log in the range is worth a look; past the alarm threshold recently is an alert. */
export function errorLogStatus(total: number, recent: number): TileStatus {
  if (recent > HEALTH_THRESHOLDS.failingErrorLogs) return 'alert';
  return total > 0 ? 'watch' : 'ok';
}
